import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import DocumentTitle from 'react-document-title'
import qs from 'qs'
import { showSectionSelectionModal } from '../../actions/modals'
import { updatePreviewItem } from '../../actions/previewItem'
import SearchProviderContainer from './SearchProviderContainer'
import Button from '../../components/uiKit/Button'
import TopPageHeader from '../../components/pageHeaders/TopPageHeader'
import StickyPageHeader from '../../components/pageHeaders/StickyPageHeader'
import SearchResultsCard from '../../components/search/SearchResultsCard'
import SearchResultsFolderCard from '../../components/search/SearchResultsFolderCard'
import SearchResultsRepoCard from '../../components/search/SearchResultsRepoCard'
import CategoryFilters from '../../components/search/CategoryFilters'
import SearchResultsPreviewPanel from '../../components/search/SearchResultsPreviewPanel'
import Icon from '../../components/misc/Icons'
import { getQuestionBodyLabel } from '../../utils/getQuestionBodyLabel'
import { getHitKind } from '../../utils/search/getHitKind'
import { getRepoforId } from '../../utils/getRepoforId'
import { getLinkForRepo } from '../../utils/getLinkForRepo'
import { useSearchContext } from '../../services/search/SearchContext'
import { useSupplementarySearch } from '../../services/search/SupplementarySearchContext'
import { recordRecentSearch } from '../../utils/search/recentSearches'
import analytics from '../../utils/cdpAnalytics'

const updateAfter = 700
const createURL = (state) => `?${qs.stringify({ query: state.query })}`
const searchStateToUrl = (props, searchState) => (
  searchState ? `${props.location.pathname}${createURL(searchState)}` : ''
)
const urlToSearchState = (location) => qs.parse(location.search.slice(1))

// ─── Search input ──────────────────────────────────────────────────────────────

function SearchInput() {
  const { currentRefinement, refine } = useSearchContext()
  return (
    <input
      type='text'
      placeholder='Search'
      value={currentRefinement}
      onChange={(e) => refine(e.target.value)}
      autoComplete='off'
      className='topBar-search'
      id='searchInputField'
      onKeyDown={(e) => {
        if (e.keyCode === 13) {
          // Any query committed directly on the results page is a content search.
          recordRecentSearch(currentRefinement)
          document.getElementById('searchInputField').blur()
        }
      }}
    />
  )
}

// ─── Router history bridge (react-router v4 — no useHistory) ──────────────────

const RouterHistoryContext = React.createContext({ history: { push: () => {} } })
const RouterHistoryBridge = withRouter(({ history, children }) => (
  <RouterHistoryContext.Provider value={{ history }}>
    {children}
  </RouterHistoryContext.Provider>
))
function useRouterHistory() {
  return React.useContext(RouterHistoryContext)
}

// ─── Category-filter context ──────────────────────────────────────────────────
// Lives on SearchResultsContainer's state. Descendants (the header pill bar
// and the per-category groups) subscribe via useCategoryFilters().

const DEFAULT_CATEGORY_FILTERS = {
  enabled: {
    repos: true, folders: true, sets: true, questions: true,
  },
  onToggle: () => {},
}
const CategoryFiltersContext = React.createContext(DEFAULT_CATEGORY_FILTERS)
function useCategoryFilters() {
  return React.useContext(CategoryFiltersContext)
}

// ─── Active-result context ────────────────────────────────────────────────────
// Tracks which row in the (flattened) results list is currently active so
// keyboard navigation, the highlight class, and the right-column preview can
// stay in sync.

const ActiveResultContext = React.createContext({
  activeId: null,
  activeHit: null,
  flatHits: [],
})
function useActiveResult() {
  return React.useContext(ActiveResultContext)
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getAdditiveBlock(additiveResults, providerId) {
  if (!Array.isArray(additiveResults)) return null
  return additiveResults.find((r) => r.providerId === providerId) || null
}

function getAdditiveHits(additiveResults, providerId) {
  const block = getAdditiveBlock(additiveResults, providerId)
  return (block && Array.isArray(block.hits)) ? block.hits : []
}

function getLocalIndexResult(additiveResults, providerId, indexName) {
  const block = getAdditiveBlock(additiveResults, providerId)
  const ir = block && block.indexResults
  if (!ir) return null
  return ir[indexName] || null
}

// ─── Section group header (Library / Navigation / Help) ───────────────────────
// Rendered like the dropdown sectionHeader: icon on the left, uppercase label.

function SectionGroupHeader({ icon, label }) {
  return (
    <div className='searchResults-sectionGroupHeader'>
      <div className='searchResults-sectionGroupHeader-icon'>
        <Icon name={icon} />
      </div>
      <span className='searchResults-sectionGroupHeader-label'>{label}</span>
    </div>
  )
}

// ─── Source indicator (Algolia / Local·Fuse / Local·BM25) ─────────────────────

function SourceLabel({ label }) {
  return <div className='searchResults-sourceLabel'>{label}</div>
}

// ─── Individual hit renderer ───────────────────────────────────────────────────

// Stable id used in the flat-hits list and the active-row wrapper. `source`
// disambiguates the same logical item appearing across Algolia + local
// engines so each row in the visible list still has a unique key.
function buildHitRowId(source, hit) {
  const id = (hit && hit.objectID) || (hit && hit.id) || ''
  return `${source}-${id}`
}

// Highlight + auto-scroll wrapper around each result card.
function ActiveResultRow({ rowId, children }) {
  const { activeId } = useActiveResult()
  const ref = React.useRef(null)
  const isActive = activeId === rowId
  React.useEffect(() => {
    if (isActive && ref.current && typeof ref.current.scrollIntoView === 'function') {
      ref.current.scrollIntoView({ block: 'nearest' })
    }
  }, [isActive])
  return (
    <div
      ref={ref}
      className={`searchResults-rowWrapper${isActive ? ' searchResults-rowWrapper--active' : ''}`}
    >
      {children}
    </div>
  )
}

const Hit = withRouter(({ item, play, rowId }) => {
  const kind = getHitKind(item)
  let card = null
  if (kind === 'set') {
    card = (
      <SearchResultsCard
        set
        item={item}
        title={item.name}
        play={() => { play(item) }}
      />
    )
  } else if (kind === 'question') {
    card = (
      <SearchResultsCard
        question
        item={item}
        title={getQuestionBodyLabel(item)}
        play={() => { play(item) }}
      />
    )
  } else if (kind === 'folder') {
    card = <SearchResultsFolderCard folder={item} />
  } else if (kind === 'repo') {
    card = <SearchResultsRepoCard repo={item} />
  }
  if (!card) return null
  return <ActiveResultRow rowId={rowId}>{card}</ActiveResultRow>
})

function HitList({ hits, source, play }) {
  if (!hits || hits.length === 0) return null
  return (
    <div className='searchResults-list'>
      {hits.map((hit) => (
        <Hit
          item={hit}
          key={buildHitRowId(source, hit)}
          rowId={buildHitRowId(source, hit)}
          play={play}
        />
      ))}
    </div>
  )
}

// ─── Library combined lists (score-ordered, not split by category) ────────────
// The library is rendered as combined lists per engine, ordered by score —
// NOT split into per-category groups. Category filters still apply: a hit is
// shown only when its kind (repo/folder/set/question) is enabled.

// Whether a hit's kind is currently enabled in the category filters.
function isHitEnabled(hit, enabled) {
  const kind = getHitKind(hit)
  if (kind === 'repo') return !!enabled.repos
  if (kind === 'folder') return !!enabled.folders
  if (kind === 'set') return !!enabled.sets
  if (kind === 'question') return !!enabled.questions
  return false
}

// Cross-index Algolia comparator built on _rankingInfo. The query-match-quality
// fields are comparable across the repos/folders/sets_questions indexes (unlike
// per-index userScore, which is only a defensive final tie-break). Applied as a
// tuple: nbTypos asc, words desc, proximityDistance asc, nbExactWords desc,
// userScore desc. Missing fields are treated neutrally.
function compareByRankingInfo(a, b) {
  const ra = (a && a._rankingInfo) || {}
  const rb = (b && b._rankingInfo) || {}
  const num = (v, fallback) => (typeof v === 'number' ? v : fallback)
  const typos = num(ra.nbTypos, Infinity) - num(rb.nbTypos, Infinity)
  if (typos !== 0) return typos
  const words = num(rb.words, -Infinity) - num(ra.words, -Infinity)
  if (words !== 0) return words
  const prox = num(ra.proximityDistance, Infinity) - num(rb.proximityDistance, Infinity)
  if (prox !== 0) return prox
  const exact = num(rb.nbExactWords, -Infinity) - num(ra.nbExactWords, -Infinity)
  if (exact !== 0) return exact
  return num(rb.userScore, -Infinity) - num(ra.userScore, -Infinity)
}

// Fuse __localScore is a normalised 0..1 distance (0 = perfect) → ascending.
function compareByLocalScore(a, b) {
  const sa = a && typeof a.__localScore === 'number' ? a.__localScore : Infinity
  const sb = b && typeof b.__localScore === 'number' ? b.__localScore : Infinity
  return sa - sb
}

// BM25 __minisearchScore is higher = stronger → descending. (BM25 IDF is
// per-corpus so cross-index ordering is approximate — a known caveat.)
function compareByMinisearchScore(a, b) {
  const sa = a && typeof a.__minisearchScore === 'number' ? a.__minisearchScore : -Infinity
  const sb = b && typeof b.__minisearchScore === 'number' ? b.__minisearchScore : -Infinity
  return sb - sa
}

// Merge all Algolia index hits into one enabled + score-ordered list.
function collectAlgoliaHits(indexResults, enabled) {
  const all = [
    ...((indexResults.repos && indexResults.repos.hits) || []),
    ...((indexResults.folders && indexResults.folders.hits) || []),
    ...((indexResults.sets_questions && indexResults.sets_questions.hits) || []),
  ]
  return all.filter((h) => isHitEnabled(h, enabled)).sort(compareByRankingInfo)
}

// Merge a local provider's index hits into one enabled + score-ordered list.
function collectLocalHits(additiveResults, providerId, enabled, compareFn) {
  const all = []
  ;['repos', 'folders', 'sets_questions'].forEach((idx) => {
    const r = getLocalIndexResult(additiveResults, providerId, idx)
    if (r && Array.isArray(r.hits)) all.push(...r.hits)
  })
  return all.filter((h) => isHitEnabled(h, enabled)).sort(compareFn)
}

// loadMore handlers for any enabled Algolia index that still has more pages.
function collectAlgoliaLoadMores(indexResults, enabled) {
  const out = []
  const consider = (idx, isEnabled) => {
    const r = indexResults[idx]
    if (isEnabled && r && r.hasMore && typeof r.loadMore === 'function') out.push(r.loadMore)
  }
  consider('repos', enabled.repos)
  consider('folders', enabled.folders)
  consider('sets_questions', enabled.sets || enabled.questions)
  return out
}

// ─── Library section ───────────────────────────────────────────────────────────

function LibrarySection({ play }) {
  const { indexResults, currentRefinement } = useSearchContext()
  const { additiveResults } = useSupplementarySearch()
  const { enabled } = useCategoryFilters()
  const anyEnabled = enabled.repos || enabled.folders || enabled.sets || enabled.questions

  const algoliaHits = collectAlgoliaHits(indexResults, enabled)
  const fuseHits = collectLocalHits(additiveResults, 'local-content', enabled, compareByLocalScore)
  const bm25Hits = collectLocalHits(additiveResults, 'minisearch-content', enabled, compareByMinisearchScore)
  const total = algoliaHits.length + fuseHits.length + bm25Hits.length

  if (!currentRefinement || !anyEnabled || total === 0) return null

  const activeSources = [algoliaHits, fuseHits, bm25Hits].filter((h) => h.length > 0).length
  const multiSource = activeSources > 1
  const algoliaLoadMores = collectAlgoliaLoadMores(indexResults, enabled)

  return (
    <React.Fragment>
      <SectionGroupHeader icon='library' label='Library' />
      {algoliaHits.length > 0 && (
        <React.Fragment>
          {multiSource && <SourceLabel label='Algolia' />}
          <HitList hits={algoliaHits} source='a' play={play} />
          {algoliaLoadMores.length > 0 && (
            <div className='searchResults-list-showMoreContainer'>
              <Button
                label='Show more results'
                size='large'
                onClickFunction={() => algoliaLoadMores.forEach((fn) => fn())}
              />
            </div>
          )}
        </React.Fragment>
      )}
      {fuseHits.length > 0 && (
        <React.Fragment>
          {multiSource && <SourceLabel label='Local · Fuse' />}
          <HitList hits={fuseHits} source='fuse' play={play} />
        </React.Fragment>
      )}
      {bm25Hits.length > 0 && (
        <React.Fragment>
          {multiSource && <SourceLabel label='Local · BM25' />}
          <HitList hits={bm25Hits} source='bm25' play={play} />
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

// ─── External links section ────────────────────────────────────────────────────

function ExternalLinksSection() {
  const { currentRefinement } = useSearchContext()
  const { additiveResults } = useSupplementarySearch()
  const hits = getAdditiveHits(additiveResults, 'external-help')
  if (!currentRefinement || hits.length === 0) return null
  return (
    <React.Fragment>
      <SectionGroupHeader icon='book-open' label='Help' />
      <div className='searchResults-list searchResults-list--external'>
        {hits.map((hit) => (
          <ActiveResultRow key={`ext-${hit.objectID}`} rowId={`ext-${hit.objectID}`}>
            <a
              href={hit.url}
              target='_blank'
              rel='noopener noreferrer'
              className='searchResults-externalItem'
            >
              <Icon name='web' />
              <span className='searchResults-externalItem-label'>{hit.label}</span>
              {hit.__localOrigin && hit.__localEngine !== 'minisearch' && (
                <Icon name='local-source' className='searchResults-localBadge' />
              )}
              {hit.__localOrigin && hit.__localEngine === 'minisearch' && (
                <Icon name='local-source-bm25' className='searchResults-localBadge searchResults-localBadge--bm25' />
              )}
            </a>
          </ActiveResultRow>
        ))}
      </div>
    </React.Fragment>
  )
}

// ─── Page header ──────────────────────────────────────────────────────────────

// Count how many `sets_questions` hits across all three sources (Algolia +
// Fuse + BM25) match a given kind. Used by the results count so the figure
// reflects filter state.
function countSetsQuestionsByKind(indexResults, additiveResults, kind) {
  const isKind = (h) => getHitKind(h) === kind
  const algoliaHits = (indexResults.sets_questions && indexResults.sets_questions.hits) || []
  const fuseHits = (getLocalIndexResult(additiveResults, 'local-content', 'sets_questions')?.hits) || []
  const bm25Hits = (getLocalIndexResult(additiveResults, 'minisearch-content', 'sets_questions')?.hits) || []
  return (
    algoliaHits.filter(isKind).length +
    fuseHits.filter(isKind).length +
    bm25Hits.filter(isKind).length
  )
}

function countSimpleCategory(indexResults, additiveResults, indexName) {
  const algoliaCount = (indexResults[indexName]?.nbHits) || 0
  const fuseCount = getLocalIndexResult(additiveResults, 'local-content', indexName)?.nbHits || 0
  const bm25Count = getLocalIndexResult(additiveResults, 'minisearch-content', indexName)?.nbHits || 0
  return algoliaCount + fuseCount + bm25Count
}

function ResultsPageHeader() {
  const { indexResults, currentRefinement } = useSearchContext()
  const { additiveResults } = useSupplementarySearch()
  const { enabled, onToggle } = useCategoryFilters()

  const categoryCounts = {
    repos: countSimpleCategory(indexResults, additiveResults, 'repos'),
    folders: countSimpleCategory(indexResults, additiveResults, 'folders'),
    sets: countSetsQuestionsByKind(indexResults, additiveResults, 'set'),
    questions: countSetsQuestionsByKind(indexResults, additiveResults, 'question'),
  }
  const libraryTotal = Object.keys(categoryCounts).reduce(
    (acc, key) => acc + (enabled[key] ? categoryCounts[key] : 0), 0,
  )
  const ancillaryTotal = (
    getAdditiveHits(additiveResults, 'navigation').length +
    getAdditiveHits(additiveResults, 'external-help').length
  )
  const total = libraryTotal + ancillaryTotal

  let searchResultsText
  if (currentRefinement) {
    if (total === 1) searchResultsText = '1 Search Result'
    else if (total > 1) searchResultsText = `${total} Search Results`
    else searchResultsText = 'No Results'
  }

  const headerRow = currentRefinement ? (
    <div className='searchResults-headerRow'>
      <span className='searchResults-headerRow-count'>{searchResultsText}</span>
      <CategoryFilters enabled={enabled} onToggle={onToggle} />
    </div>
  ) : searchResultsText

  return [
    <TopPageHeader
      key='topHeader'
      location='searchResults'
      header={<SearchInput />}
      headerInfoRow={headerRow}
    />,
    <StickyPageHeader
      key='stickyHeader'
      location='searchResults'
      header={currentRefinement}
      actionGroup={headerRow}
    />,
  ]
}

// ─── Flat-list builder + keyboard nav controller ─────────────────────────────

function getPathForHit(rowKind, hit) {
  if (rowKind === 'nav') return hit && hit.path
  if (rowKind === 'external') return hit && hit.url
  if (rowKind === 'repo') return getLinkForRepo(hit)
  if (rowKind === 'folder') {
    if (!hit) return null
    if (hit.repo) {
      const parentRepo = getRepoforId(hit.repo)
      if (parentRepo) return `${getLinkForRepo(parentRepo)}/${hit.id}`
      return `/packs/${hit.repo}/${hit.id}`
    }
    return `/library/${hit.id}`
  }
  if (rowKind === 'set') {
    const id = hit && (hit.id || hit._id)
    return id ? `/set/${id}` : null
  }
  if (rowKind === 'question') {
    const id = hit && (hit.id || hit._id)
    return id ? `/question/${id}` : null
  }
  return null
}

function appendLibraryRows(out, source, hits) {
  if (!Array.isArray(hits)) return
  hits.forEach((hit) => {
    const kind = getHitKind(hit)
    if (!kind) return
    out.push({
      id: buildHitRowId(source, hit),
      kind,
      hit,
      path: getPathForHit(kind, hit),
      external: false,
    })
  })
}

// Builds the flat ordered list of currently-visible result rows, in the same
// order the page renders them: the combined Library lists (Algolia, then Fuse,
// then BM25 — each enabled + score-ordered, matching LibrarySection), then the
// external help links.
function buildFlatHits(indexResults, additiveResults, enabled) {
  const out = []

  appendLibraryRows(out, 'a', collectAlgoliaHits(indexResults, enabled))
  appendLibraryRows(
    out, 'fuse',
    collectLocalHits(additiveResults, 'local-content', enabled, compareByLocalScore),
  )
  appendLibraryRows(
    out, 'bm25',
    collectLocalHits(additiveResults, 'minisearch-content', enabled, compareByMinisearchScore),
  )

  const externalHits = getAdditiveHits(additiveResults, 'external-help')
  externalHits.forEach((hit) => {
    out.push({
      id: `ext-${hit.objectID}`,
      kind: 'external',
      hit,
      path: hit && hit.url,
      external: true,
    })
  })

  return out
}

// True when the keyboard event happens inside an editable field (the search
// input, a contenteditable area, etc.) — in those cases ArrowUp/ArrowDown
// belong to the editor, not to the results list.
function isEditableTarget(target) {
  if (!target) return false
  const tag = (target.tagName || '').toUpperCase()
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  return false
}

// react-redux v5 has no hooks, so the store-derived bits (previewItem flag +
// updatePreviewItem dispatcher) are injected via connect (see bottom of fn).
function ActiveResultProviderComponent({ children, previewItem, updatePreviewItem: dispatchUpdatePreviewItem }) {
  const { indexResults, currentRefinement } = useSearchContext()
  const { additiveResults } = useSupplementarySearch()
  const { enabled } = useCategoryFilters()
  const { history } = useRouterHistory()
  // When the lightweight preview modal is open it owns the keyboard (arrows /
  // Esc / P / Q), so the results-page list handler must stand down.
  const isPreviewModalOpen = !!previewItem

  // When the query is empty there are no results to navigate or preview — force
  // an empty list so the right panel falls back to its empty state (otherwise a
  // stale active hit from the previous query keeps its preview mounted).
  const flatHits = React.useMemo(
    () => (currentRefinement ? buildFlatHits(indexResults, additiveResults, enabled) : []),
    [currentRefinement, indexResults, additiveResults, enabled],
  )

  const [activeIndex, setActiveIndex] = React.useState(0)

  // Reset to the first row whenever the query changes or the list shrinks.
  const flatIds = React.useMemo(() => flatHits.map((h) => h.id).join('|'), [flatHits])
  React.useEffect(() => {
    setActiveIndex(0)
  }, [currentRefinement, flatIds])

  // With an empty result set there are no rows to navigate or preview, so keep
  // the search input focused — the user is most likely still refining the query
  // (this also re-focuses after arriving from the dropdown, which blurs the
  // input on navigation so list arrow-keys can take over when results exist).
  const isEmptyResultSet = flatHits.length === 0
  React.useEffect(() => {
    if (!isEmptyResultSet) return
    const input = document.getElementById('searchInputField')
    if (input && document.activeElement !== input && typeof input.focus === 'function') {
      input.focus()
    }
  }, [isEmptyResultSet])

  const clampedIndex = flatHits.length === 0 ?
    -1 :
    Math.min(Math.max(activeIndex, 0), flatHits.length - 1)
  const activeRow = clampedIndex >= 0 ? flatHits[clampedIndex] : null

  React.useEffect(() => {
    function onKeyDown(e) {
      // The lightweight preview modal, when open, handles its own keys.
      if (isPreviewModalOpen) return
      // Allow Shift+Enter (opens the preview modal); block other Ctrl/Meta combos.
      const isShiftEnter = e.shiftKey && (e.key === 'Enter' || e.keyCode === 13)
      if (!isShiftEnter && (e.altKey || e.ctrlKey || e.metaKey)) return
      // ArrowDown while the search input is focused → move focus into the list
      // (re-focus the first row).
      const isSearchInput = e.target && e.target.id === 'searchInputField'
      if (isSearchInput && e.key === 'ArrowDown') {
        if (flatHits.length === 0) return
        e.preventDefault()
        if (typeof e.target.blur === 'function') e.target.blur()
        setActiveIndex(0)
        return
      }
      if (isEditableTarget(e.target) && e.key !== 'Enter') return
      if (flatHits.length === 0) return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((prev) => Math.min(prev + 1, flatHits.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        // ArrowUp from the first row → focus the search input.
        if (activeIndex <= 0) {
          const input = document.getElementById('searchInputField')
          if (input && typeof input.focus === 'function') input.focus()
          return
        }
        setActiveIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter') {
        // The search input also listens for Enter to blur itself — only
        // hijack when the user isn't typing into it.
        if (isEditableTarget(e.target)) return
        const row = flatHits[Math.min(Math.max(activeIndex, 0), flatHits.length - 1)]
        if (!row) return
        // Shift+Enter → lightweight preview modal for local set/question rows.
        if (isShiftEnter) {
          if (row.kind === 'set' || row.kind === 'question') {
            const itemId = row.hit && (row.hit.id || row.hit._id)
            if (itemId) {
              e.preventDefault()
              // Drop focus from the search input so the preview modal's global
              // key handlers (←→ / P / Q / Esc) aren't swallowed by the field.
              if (isEditableTarget(e.target) && typeof e.target.blur === 'function') {
                e.target.blur()
              }
              dispatchUpdatePreviewItem(itemId)
            }
          }
          return
        }
        if (!row.path) return
        e.preventDefault()
        if (row.external) {
          window.open(row.path, '_blank', 'noopener,noreferrer')
        } else {
          history.push(row.path)
        }
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [flatHits, activeIndex, history, dispatchUpdatePreviewItem, isPreviewModalOpen])

  const value = React.useMemo(() => ({
    activeId: activeRow ? activeRow.id : null,
    activeHit: activeRow ? activeRow.hit : null,
    activeKind: activeRow ? activeRow.kind : null,
    flatHits,
  }), [activeRow, flatHits])

  return (
    <ActiveResultContext.Provider value={value}>
      {children}
    </ActiveResultContext.Provider>
  )
}

const ActiveResultProvider = connect(
  (state) => ({ previewItem: state.previewItem }),
  { updatePreviewItem },
)(ActiveResultProviderComponent)

// Reads the active hit and feeds it to the right-column preview. Lives here
// (not in the JSX shell) because it needs access to the search contexts.
function ResultsRightPanel() {
  const { activeHit, activeKind } = useActiveResult()
  return <SearchResultsPreviewPanel activeHit={activeHit} activeKind={activeKind} />
}

// ─── SearchResultsContainer ───────────────────────────────────────────────────

class SearchResultsContainer extends Component {
  constructor(props) {
    super(props)
    this.onSearchStateChange = this.onSearchStateChange.bind(this)
    this.toggleCategory = this.toggleCategory.bind(this)
    this.state = {
      searchState: urlToSearchState(props.location),
      enabledCategories: {
        repos: true, folders: true, sets: true, questions: true,
      },
    }
  }

  componentDidMount() {
    analytics.page('Search Results')
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (props.location !== this.props.location) {
      this.setState({ searchState: urlToSearchState(props.location) })
    }
  }

  onSearchStateChange(searchState) {
    clearTimeout(this.debouncedSetState)
    this.debouncedSetState = setTimeout(() => {
      window.history.pushState(
        searchState,
        null,
        searchStateToUrl(this.props, searchState),
      )
    }, updateAfter)
    this.setState({ searchState })
  }

  toggleCategory(key) {
    this.setState((prev) => ({
      enabledCategories: {
        ...prev.enabledCategories,
        [key]: !prev.enabledCategories[key],
      },
    }))
  }

  render() {
    const { showSectionSelectionModal: playCb } = this.props
    const categoryFiltersValue = {
      enabled: this.state.enabledCategories,
      onToggle: this.toggleCategory,
    }
    return (
      <DocumentTitle title='Search Results - Plickers'>
        <div className='page searchResults page--wideLayout'>
          <div className='page-leftPanel' />
          <SearchProviderContainer
            searchState={this.state.searchState}
            onSearchStateChange={this.onSearchStateChange}
            createURL={createURL}
            hitsPerPage={100}
          >
            <RouterHistoryBridge>
              <CategoryFiltersContext.Provider value={categoryFiltersValue}>
                <ActiveResultProvider>
                  <div className='page-centerPanel'>
                    <ResultsPageHeader />
                    <div className='searchResults-content'>
                      <LibrarySection play={playCb} />
                      <ExternalLinksSection />
                    </div>
                    <div className='searchResults-shortcutHelp'>
                      <span className='searchResults-shortcutHelp-item'>
                        <kbd>↑↓</kbd>
                        {' navigate'}
                      </span>
                      <span className='searchResults-shortcutHelp-item'>
                        <kbd>Enter</kbd>
                        {' open'}
                      </span>
                      <span className='searchResults-shortcutHelp-item'>
                        <kbd>Shift+Enter</kbd>
                        {' preview'}
                      </span>
                      <span className='searchResults-shortcutHelp-item'>
                        <kbd>⌘K</kbd>
                        {' back to search'}
                      </span>
                    </div>
                  </div>
                  <div className='page-rightPanel'>
                    <ResultsRightPanel />
                  </div>
                </ActiveResultProvider>
              </CategoryFiltersContext.Provider>
            </RouterHistoryBridge>
          </SearchProviderContainer>
        </div>
      </DocumentTitle>
    )
  }
}

export default withRouter(connect(
  (state) => ({
    permissions: state.permissions,
  }),
  { showSectionSelectionModal },
)(SearchResultsContainer))
